import { useMemo, useRef, useState } from "react";
import type { TeamMember } from "@closing/shared";
import { useDirectory } from "../lib/directory";

/**
 * Textarea with @mention autocomplete. Typing "@" plus letters opens a
 * dropdown of the team directory; selecting inserts an `@[Name](email)`
 * token (the CRM's cross-tool convention). The raw token stays visible in
 * the textarea — comments render it prettily via segmentText.
 */
export default function MentionInput({
  value,
  onChange,
  onSubmit,
  placeholder,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  const directory = useDirectory();
  const taRef = useRef<HTMLTextAreaElement>(null);
  const [mentionQuery, setMentionQuery] = useState<{ start: number; text: string } | null>(null);
  const [highlight, setHighlight] = useState(0);

  const candidates = useMemo(() => {
    if (!mentionQuery) return [];
    const q = mentionQuery.text.toLowerCase();
    return directory
      .filter((t) => t.displayName.toLowerCase().includes(q) || t.email.startsWith(q))
      .slice(0, 6);
  }, [directory, mentionQuery]);

  function refreshMentionState(text: string, caret: number) {
    // An active mention query is an "@" not preceded by a word char, with
    // only name-ish chars between it and the caret.
    const upToCaret = text.slice(0, caret);
    const at = upToCaret.lastIndexOf("@");
    if (at < 0 || (at > 0 && /\w/.test(upToCaret[at - 1]!))) {
      setMentionQuery(null);
      return;
    }
    const fragment = upToCaret.slice(at + 1);
    if (/^[\w .'-]{0,30}$/.test(fragment)) {
      setMentionQuery({ start: at, text: fragment });
      setHighlight(0);
    } else {
      setMentionQuery(null);
    }
  }

  function insertMention(member: TeamMember) {
    if (!mentionQuery || !taRef.current) return;
    const caret = taRef.current.selectionStart;
    const token = `@[${member.displayName}](${member.email}) `;
    const next = value.slice(0, mentionQuery.start) + token + value.slice(caret);
    onChange(next);
    setMentionQuery(null);
    // Restore focus with the caret after the inserted token.
    requestAnimationFrame(() => {
      const pos = mentionQuery.start + token.length;
      taRef.current?.setSelectionRange(pos, pos);
      taRef.current?.focus();
    });
  }

  return (
    <div className="relative">
      {mentionQuery && candidates.length > 0 && (
        <div className="absolute bottom-full left-0 z-10 mb-1 w-64 overflow-hidden rounded-md border border-line bg-white shadow-lg">
          {candidates.map((c, i) => (
            <button
              key={c.email}
              onMouseDown={(e) => {
                e.preventDefault(); // keep textarea focus
                insertMention(c);
              }}
              className={`block w-full px-3 py-1.5 text-left text-[13px] ${
                i === highlight ? "bg-paper text-ink" : "text-ink-soft"
              }`}
            >
              {c.displayName}
              <span className="ml-2 font-mono text-[10px] text-faint">{c.email}</span>
            </button>
          ))}
        </div>
      )}
      <textarea
        ref={taRef}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        rows={2}
        onChange={(e) => {
          onChange(e.target.value);
          refreshMentionState(e.target.value, e.target.selectionStart);
        }}
        onKeyDown={(e) => {
          if (mentionQuery && candidates.length > 0) {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setHighlight((h) => (h + 1) % candidates.length);
              return;
            }
            if (e.key === "ArrowUp") {
              e.preventDefault();
              setHighlight((h) => (h - 1 + candidates.length) % candidates.length);
              return;
            }
            if (e.key === "Enter" || e.key === "Tab") {
              e.preventDefault();
              insertMention(candidates[highlight] ?? candidates[0]!);
              return;
            }
            if (e.key === "Escape") {
              setMentionQuery(null);
              return;
            }
          }
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            onSubmit();
          }
        }}
        onClick={(e) =>
          refreshMentionState(value, (e.target as HTMLTextAreaElement).selectionStart)
        }
        className="w-full resize-none rounded-md border border-line bg-white px-3 py-2 text-[13px] outline-none placeholder:text-faint focus:border-line-strong"
      />
    </div>
  );
}
